/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

const collectionNavigationSelect = document.querySelector(
  ".collection-navigation-select",
);

if (collectionNavigationSelect) {
  collectionNavigationSelect.addEventListener("change", function (event) {
    if (event.target.classList.contains("bundle-select")) {
      return;
    }
    const selectedValue = event.target.value;
    window.location.href = selectedValue;
  });
}

/* Cart new elements click handler */
document
  .querySelector(".shopify-section--mini-cart")
  .addEventListener("click", (event) => {
    const eventPath = event.composedPath
      ? event.composedPath().slice(0, -2)
      : [];

    if (eventPath.some((node) => node instanceof DocumentFragment)) return;

    for (const element of eventPath) {
      if (!element.classList) continue;

      if (element.classList.contains("line-item-remove-container")) {
        const lineItem = element?.closest(".line-item");
        if (!lineItem) continue;

        const removeButton = lineItem?.querySelector(
          ".line-item__remove-button",
        );
        if (!removeButton) continue;

        lineItem.closest("form").setAttribute("data-loading", "");

        if (
          removeButton.tagName === "BUTTON" &&
          removeButton.hasAttribute("data-bundle-id")
        ) {
          const bundleId = removeButton.getAttribute("data-bundle-id");
          fetch(window.Shopify.routes.root + "cart.js")
            .then((response) => response.json())
            .then(({ items }) => {
              const updates = {};

              items.forEach((it) => {
                if (it.properties._bundle_id === bundleId) {
                  updates[it.key] = 0;
                }
              });

              return fetch(window.Shopify.routes.root + "cart/update.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ updates }),
              }).then((response) =>
                response.json().then((json) => {
                  setTimeout(async () => {
                    const updatedCartContent = await freeGiftHandler(json);
                    console.log("Cart new elements click handler");
                    document.documentElement.dispatchEvent(
                      new CustomEvent("cart:refresh", {
                        bubbles: true,
                        detail: {
                          cart: updatedCartContent,
                        },
                      }),
                    );

                    document.getElementById("mini-cart").open = true;
                  }, 100);
                }),
              );
            });
        } else {
          removeButton.dispatchEvent(
            new MouseEvent("click", { bubbles: true, cancelable: true }),
          );
        }

        break;
      } else if (element.classList.contains("subscription-incentive-button")) {
        const lineItem = element?.closest(".line-item");
        if (!lineItem) continue;

        const sellingMethodSelect = lineItem.querySelector(
          ".line-item__subscription-select",
        );
        if (!sellingMethodSelect) continue;

        event.preventDefault();
        const quantitySelector = lineItem.querySelector(".quantity-selector");
        quantitySelector.setAttribute("data-loading", "");

        const currentOption = sellingMethodSelect.selectedOptions[0];

        sellingMethodSelect.value = sellingMethodSelect.querySelector(
          "[data-subscription]",
        ).value;

        if (currentOption.value !== sellingMethodSelect.value) {
          sellingMethodSelect.dispatchEvent(
            new Event("change", { bubbles: true, cancelable: true }),
          );
        }

        break;
      } else if (element.classList.contains("recommendation-atc")) {
        const recommendationCard = element.closest(".cart-recommendation-card");
        if (!recommendationCard) continue;

        const variantId = parseInt(
          recommendationCard.getAttribute("data-variant-id"),
        );
        if (!variantId) continue;

        addRecommendationProductToCart(variantId);

        break;
      }
    }
  });

/* Cart subscription select handler */
document
  .querySelector(".shopify-section--mini-cart")
  .addEventListener("change", (event) => {
    const eventPath = event.composedPath
      ? event.composedPath().slice(0, -2)
      : [];

    if (eventPath.some((node) => node instanceof DocumentFragment)) return;

    for (const element of eventPath) {
      const changeAction = element?.getAttribute("data-change-action");
      if (!changeAction) continue;

      if (changeAction === "update-line-selling-plan") {
        updateLineSellingPlan(element);
        break;
      }
    }
  });

async function updateLineSellingPlan(sellingMethodSelect) {
  const lineItem = sellingMethodSelect.closest(".line-item");
  const variantId = parseInt(lineItem.getAttribute("data-variant-id"));
  const quantity = parseInt(lineItem.getAttribute("data-quantity"));
  const lineKey = lineItem.getAttribute("data-line-key");
  const selectedOption = lineItem.querySelector('select option:checked')
  const sellingPlanId = parseInt(selectedOption.getAttribute('data-option-selling-plan-id'));

  const cartForm = sellingMethodSelect.closest("form");
  cartForm.style.pointerEvents = "none";
  const subscriptionValue = sellingMethodSelect.options[1].value;
  if (sellingMethodSelect.value === sellingMethodSelect.options[0].value) {
    cartForm.setAttribute("data-loading", "");
  }

  const addToCartBody = {
    items: [
      {
        id: variantId,
        quantity: quantity,
      },
    ],
  };

  if (sellingMethodSelect.value === subscriptionValue) {
    addToCartBody.items[0].selling_plan = sellingPlanId;
  }

  try {
    const cartUpdateJson = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ updates: { [lineKey]: 0 } }),
    }).then((response) => response.json());

    const addToCartJson = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(addToCartBody),
    }).then((response) => response.json());

    const updatedCartContent = await freeGiftHandler();

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart: updatedCartContent,
        },
      }),
    );
  } catch (error) {
    console.error("Error updating the line in the cart", error);
  }
}

async function addRecommendationProductToCart(variantId) {
  const addToCartBody = {
    items: [
      {
        id: variantId,
        quantity: 1,
      },
    ],
  };

  try {
    const addToCartJson = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(addToCartBody),
    }).then((response) => response.json());

    const updatedCartContent = await freeGiftHandler();

    setTimeout(() => {
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: updatedCartContent,
          },
        }),
      );

      document.getElementById("mini-cart").open = true;
    }, 1800);
  } catch (error) {
    console.error("Error adding the recommendation product to the cart", error);
  }
}

/* Blog post associated product ATC handler */
const mainArticle = document.querySelector(".shopify-section--main-article");
if (mainArticle) {
  mainArticle.addEventListener("click", (event) => {
    const eventPath = event.composedPath
      ? event.composedPath().slice(0, -2)
      : [];

    if (eventPath.some((node) => node instanceof DocumentFragment)) return;

    for (const element of eventPath) {
      if (!element.classList) continue;

      if (element.classList.contains("associated-product-atc")) {
        const associatedProductCard = element.closest(
          ".associated-product-container",
        );
        if (!associatedProductCard) continue;

        const variantId = parseInt(
          associatedProductCard.getAttribute("data-variant-id"),
        );
        if (!variantId) continue;

        addRecommendationProductToCart(variantId);

        break;
      }
    }
  });
}

/* Video play/pause support */
/* Mega-menu parent click behavior */
document.addEventListener("click", (event) => {
  const eventPath = event.composedPath ? event.composedPath().slice(0, -2) : [];
  if (eventPath.some((node) => node instanceof DocumentFragment)) return;

  for (const element of eventPath) {
    const clickAction = element?.getAttribute("data-click-action");
    if (!clickAction) continue;

    if (
      clickAction === "toggle-mobile-video-play" ||
      clickAction === "toggle-desktop-video-play"
    ) {
      const associatedVideo =
        clickAction === "toggle-mobile-video-play"
          ? element.parentElement.querySelector(".image-overlay__mobile--image")
          : element.parentElement.querySelector(
              ".image-overlay__desktop--image",
            );

      if (element.hasAttribute("data-play")) {
        if (associatedVideo.classList.contains("video-wrapper")) {
          associatedVideo.querySelector("iframe").contentWindow.postMessage(
            JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: "",
            }),
            "*",
          );
        } else {
          associatedVideo.pause();
        }
        element.removeAttribute("data-play");
      } else {
        if (associatedVideo.classList.contains("video-wrapper")) {
          associatedVideo
            .querySelector("iframe")
            .contentWindow.postMessage(
              JSON.stringify({ event: "command", func: "playVideo", args: "" }),
              "*",
            );
        } else {
          associatedVideo.play();
        }
        element.setAttribute("data-play", "");
      }
      break;
    } else if (clickAction === "toggle-mega-menu") {
      event.preventDefault();

      if (
        element.nextElementSibling.classList.contains("is-closing") ||
        element.nextElementSibling.classList.contains("is-opening")
      ) {
        break;
      }

      const expandedMenu = element.getAttribute("aria-expanded") === "true";

      if (expandedMenu === true) {
        element.setAttribute("aria-expanded", "false");
        element.nextElementSibling.hidden = true;
        element.nextElementSibling.style.zIndex = "-1";
      } else {
        element.setAttribute("aria-expanded", "true");
        element.nextElementSibling.hidden = false;
        element.nextElementSibling.style.zIndex = "1";
      }

      break;
    }
  }
});

/* Scrolling text section behavior - scrolling-text.liquid */
document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll(".shopify-section--scrolling-text")
    .forEach((scrollingTextSection) => {
      let totalTextWidth = 0;

      const scrollingTextList =
        scrollingTextSection.querySelectorAll(".scrolling-text");
      const scrollingTextContainerList = scrollingTextSection.querySelectorAll(
        ".scrolling-text-container",
      );

      for (const scrollingText of scrollingTextList) {
        totalTextWidth += scrollingText.offsetWidth;
      }

      const textCopyCount = Math.ceil(2000 / totalTextWidth) + 1;

      for (let i = 0; i < textCopyCount; i++) {
        for (const scrollingTextContainer of scrollingTextContainerList) {
          const containerCopy = scrollingTextContainer.cloneNode(true);
          scrollingTextContainer.parentElement.appendChild(containerCopy);
        }
      }
    });
});

/* Recommended product click handler */
document.addEventListener("click", (event) => {
  const eventPath = event.composedPath ? event.composedPath().slice(0, -2) : [];
  if (eventPath.some((node) => node instanceof DocumentFragment)) return;

  for (const element of eventPath) {
    if (!element.hasAttribute("data-recommendation-href")) continue;

    event.preventDefault();

    const recommendationHref = element.getAttribute("data-recommendation-href");
    window.location.href = recommendationHref;
  }
});

if (document.querySelector('.article')) {
  // If on a blog article hide/remove any wp-content images
  document.querySelectorAll('img[src*="wp-content"]').forEach((i) => i.remove());
  // If on a blog move image out of heading
  document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(h => {
    const onlyImg =
      h.children.length === 1 &&
      h.firstElementChild.tagName === 'IMG';

    if (onlyImg) {
      h.parentNode.insertBefore(h.firstElementChild, h);
      h.remove();
    }
  });
}

// Reviews count
const reviewNodes = document.querySelectorAll(
  ".block-reviews .rating",
);

const addReviewCount = (container, parent) => {
  const reviewsElement = container.querySelector("a");
  const span = document.createElement("span");
  span.classList.add("rating__caption");
  const hasReviews =
    reviewsElement?.getAttribute("aria-label")?.split(" ")[0] !== undefined;

  if (hasReviews) {
    const reviewsCount = reviewsElement
      .getAttribute("aria-label")
      .split(" ")[0];

    span.innerHTML = `(${reviewsCount})`;
    return span;
  } else {
    const url = `${themeVariables.routes.rootUrl}products/${container.parentElement.dataset.productHandle}#reviews`;
    span.innerHTML = `<a href="${url}">${themeVariables.strings.emptyReviews}</a>`;
    parent.classList.add("no-reviews");
    return span;
  }
};

reviewNodes.forEach((reviewNode) => {
  const reviewNodeObserver = new MutationObserver((mutationList, observer) => {
    mutationList.forEach((mutation) => {
      if (mutation.type === "childList") {
        const container = mutation.addedNodes[1];

        if (!container) return;

        const span = addReviewCount(container, reviewNode.parentElement);

        if (span) {
          reviewNode.parentElement.appendChild(span.cloneNode(true));
        }
      }
    });
  });

  reviewNodeObserver.observe(reviewNode, {
    childList: true,
  });
});

/* Sticky add to cart intersection observer */
const productSectionList = document.querySelectorAll(
  ".shopify-section--main-product",
);

if (productSectionList.length) {
  productSectionList.forEach((productSection) => {
    const mainAddToCartButton = productSection.querySelector("#AddToCart");
    const stickyAddToCartContainer = productSection.querySelector(
      ".sticky-add-to-cart-container",
    );

    if (mainAddToCartButton && stickyAddToCartContainer) {
      stickyAddToCartContainer
        .querySelector("button")
        .addEventListener("click", (event) => {
          event.preventDefault();
          const clickEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          mainAddToCartButton.dispatchEvent(clickEvent);
        });

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              stickyAddToCartContainer.setAttribute("data-visible", "");
              document.documentElement.setAttribute(
                "data-sticky-add-to-cart-visible",
                "",
              );
            } else {
              stickyAddToCartContainer.removeAttribute("data-visible");
              document.documentElement.removeAttribute(
                "data-sticky-add-to-cart-visible",
              );
            }
          });
        },
        {
          rootMargin: "-80px 0px 100000px 0px",
          threshold: 0,
        },
      );

      observer.observe(mainAddToCartButton);
    }
  });
}

/* Read more section web component definition */
const readMore = class extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.readMoreButton.addEventListener("click", () => this.onReadMoreClick());
    this.readLessButton.addEventListener("click", () => this.onReadLessClick());
  }

  disconnectedCallback() {
    this.readMoreButton.removeEventListener("click", () =>
      this.onReadMoreClick(),
    );
  }

  get readMoreButton() {
    return this.querySelector("[data-read-more]");
  }
  get readLessButton() {
    return this.querySelector("[data-read-less]");
  }
  get lessContentContainer() {
    return this.querySelector("[data-less]");
  }
  get moreContentContainer() {
    return this.querySelector("[data-more]");
  }

  onReadMoreClick() {
    this.moreContentContainer.hidden = false;
    this.lessContentContainer.hidden = true;
  }
  onReadLessClick() {
    this.moreContentContainer.hidden = true;
    this.lessContentContainer.hidden = false;
  }
};
if (!customElements.get("read-more")) {
  customElements.define("read-more", readMore);
}

/* Desktop media list web component */
const DesktopMediaList = class extends HTMLUListElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.medias.forEach((media) => {
      media.addEventListener("click", () => this.onMediaClick(media));
    });
  }

  get medias() {
    return this.querySelectorAll(".desktop-media-container");
  }
  get desktopMediaZoom() {
    return document.querySelector(".desktop-media-zoom");
  }

  onMediaClick(media) {
    const index = media.dataset.index;
    this.desktopMediaZoom.mediaContainer.setAttribute(
      "data-current-index",
      index,
    );
    this.desktopMediaZoom.openModal();
  }
};
if (!window.customElements.get("desktop-media-list")) {
  window.customElements.define("desktop-media-list", DesktopMediaList, {
    extends: "ul",
  });
}

/* Desktop media zoom web component */
const DesktopMediaZoom = class extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", (event) => {
      if (!this.mediaContainer.contains(event.target)) {
        this.closeModal();
      }
    });
    this.closeButton.addEventListener("click", () => this.closeModal());
    this.previousButton.addEventListener("click", () => this.onPreviousClick());
    this.nextButton.addEventListener("click", () => this.onNextButtonClick());
  }

  get mediaContainer() {
    return this.querySelector(".desktop-media-container");
  }
  get mediaCount() {
    return parseInt(this.mediaContainer.dataset.mediaCount);
  }
  get closeButton() {
    return this.querySelector(".drawer__close-button");
  }
  get previousButton() {
    return this.querySelector(".drawer__control-button[data-action='prev']");
  }
  get nextButton() {
    return this.querySelector(".drawer__control-button[data-action='next']");
  }

  openModal() {
    this.setAttribute("data-open", "");
    document.documentElement.classList.add("lock-all");
  }
  closeModal() {
    this.removeAttribute("data-open");
    document.documentElement.classList.remove("lock-all");
  }
  onPreviousClick() {
    const currentIndex = this.mediaContainer.dataset.currentIndex;

    let previousIndex = parseInt(currentIndex) - 1;
    if (previousIndex < 1) {
      previousIndex = this.mediaCount;
    }

    this.mediaContainer.dataset.currentIndex = previousIndex;
  }
  onNextButtonClick() {
    const currentIndex = this.mediaContainer.dataset.currentIndex;

    let nextIndex = parseInt(currentIndex) + 1;
    if (nextIndex > this.mediaCount) {
      nextIndex = 1;
    }

    this.mediaContainer.dataset.currentIndex = nextIndex;
  }
};
if (!window.customElements.get("desktop-media-zoom")) {
  window.customElements.define("desktop-media-zoom", DesktopMediaZoom);
}

function collectBundleItems(bundleId) {
  const items = [];

  document
    .querySelectorAll(`.bundle-children [data-bundle-id="${bundleId}"]`)
    .forEach((el) => {
      items.push({
        variantId: parseInt(el.dataset.variantId),
        quantity: parseInt(el.dataset.quantity),
        title: el.dataset.title,
        imageUrl: el.dataset.imageUrl,
        oneTimePrice: parseFloat(el.dataset.oneTimePrice) / 100,
        oneTimeDiscountPrice: parseFloat(el.dataset.oneTimeDiscountPrice) / 100,
        subscriptionPrice: parseFloat(el.dataset.subscriptionPrice) / 100,
      });
    });

  return items;
}

function saveBundleToLocalStorage(bundleId, items) {
  localStorage.setItem("editing_bundle_id", bundleId);
  localStorage.setItem("bundle_builder_items", JSON.stringify(items));
}

async function clearBundleItemsFromCart(bundleId, bundlePath) {
  const cart = await fetch("/cart.js").then((res) => res.json());
  const updates = {};

  cart.items.forEach((item) => {
    if (item.properties && item.properties._bundle_id === bundleId) {
      updates[item.key] = 0;
    }
  });

  if (Object.keys(updates).length === 0) return;

  const response = await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates }),
  });

  const updatedCart = await response.json();
  const updatedCartContent = await freeGiftHandler(updatedCart);

  if (window.location.pathname === bundlePath) {
    window.document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { cart: updatedCartContent },
      }),
    );
    document.getElementById("mini-cart").open = false;
  }
}

function redirectToBundleBuilder(bundleId, bundlePath) {
  if (window.location.pathname.includes(bundlePath)) {
    setTimeout(() => {
      const context = document.querySelector("bundle-builder-context");
      if (context) {
        context.dispatchEvent(
          new CustomEvent("BUNDLE_RESTORE_REQUESTED", {
            detail: { isEditing: true },
          }),
        );
      }
    }, 0);
  } else {
    setTimeout(() => {
      window.location.href = bundlePath;
    }, 100);
  }
}

document.addEventListener("click", async (e) => {
  const button = e.target.closest("[data-edit-bundle]");
  if (!button) return;

  const bundleId = button.dataset.editBundle;
  const bundlePath = button.dataset.path;

  const bundleItems = collectBundleItems(bundleId);
  saveBundleToLocalStorage(bundleId, bundleItems);

  await clearBundleItemsFromCart(bundleId, bundlePath);

  setTimeout(() => {
    redirectToBundleBuilder(bundleId, bundlePath);
  }, 100);
});

function getCookieValue(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function isFirstVisitPeriod(daysThreshold = 3) {
  const timestamp = parseInt(getCookieValue("first_visit_time"), 10);
  if (!timestamp || isNaN(timestamp)) return true;

  const now = Date.now();
  const msInDay = 24 * 60 * 60 * 1000;
  const daysPassed = (now - timestamp) / msInDay;

  return daysPassed <= daysThreshold;
}

function matchRule(rule, cartItems, cartContent, options = {}) {
  const { ignorePathCheck = false } = options;

  if (
    rule.subscription_only &&
    !cartItems.some((i) => i.selling_plan_allocation)
  )
    return false;

  if (rule.one_time_only && cartItems.some((i) => i.selling_plan_allocation))
    return false;

  if (rule.new_customers_only && !isFirstVisitPeriod(3)) return false;

  // All
  if (rule.target_skus?.length) {
    const allSkusPresent = rule.target_skus.every((sku) =>
      cartItems.some((i) => i.sku === sku),
    );
    if (!allSkusPresent) return false;
  }

  // Any
  if (rule.target_discount_codes?.length) {

    const appliedCodes = getCartDiscounts(cartContent).map((d) =>
      d.title.toLowerCase()
    );
    const targetCodes = rule.target_discount_codes.map((c) => c.toLowerCase());

    // Helper function to check if a code matches a pattern with wildcards
    const matchesPattern = (code, pattern) => {
      if (!code || !pattern) return false;

      // Convert wildcard pattern to regex
      // Escape special regex chars except * which becomes .*
      const regexPattern = pattern
        .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape special regex chars
        .replace(/\*/g, '.*'); // Convert * to .*

      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(code);
    };

    const matched = appliedCodes.some((code) =>
      targetCodes.some((pattern) => matchesPattern(code, pattern))
    );

    console.log('no discounbt match', { rule, appliedCodes })
    if (!matched) return false;
  }

  if ("minimum_cart_value" in rule) {
    if (cartContent.total_price < rule.minimum_cart_value) return false;
  }

  const totalQuantity = cartItems
    .filter((i) => !(i.properties && i.properties._free_gift))
    .reduce((sum, i) => sum + i.quantity, 0);

  if (rule.min_cart_items && totalQuantity < rule.min_cart_items) return false;

  return !(
    !ignorePathCheck &&
    rule.target_path &&
    !window.location.pathname.startsWith(rule.target_path)
  );
}

async function freeGiftHandler(cartContent) {
  const hasGiftGroups = window.giftGroups?.length > 0;

  if (!hasGiftGroups && !cartContent) {
    return await fetch("/cart.js").then((res) => res.json());
  }

  if (!hasGiftGroups) return cartContent;

  if (!cartContent) {
    cartContent = await fetch("/cart.js").then((res) => res.json());
  }

  const cartItems = cartContent.items;
  const existingGifts = cartItems.filter(
    (item) => item.properties && item.properties._free_gift,
  );

  const existingGift = existingGifts?.[0];

  const existingGiftSourcePath = existingGift?.properties?._gift_source_path;

  // find best gift by priority
  let bestGift = null;

  for (const group of window.giftGroups) {
    const { gift_variant_id, rules, gift_priority = 99 } = group; // default priority if missing
    if (!gift_variant_id || !rules || !rules.length) continue;

    const matchedRule = rules.find((rule) =>
      matchRule(rule, cartItems, cartContent, {
        ignorePathCheck: Boolean(existingGiftSourcePath),
      }),
    );

    if (matchedRule) {
      if (
        !bestGift ||
        gift_priority < bestGift.gift_priority // select gift with lowest priority number
      ) {
        bestGift = { variant_id: gift_variant_id, gift_priority };
      }
    }
  }

  const giftToKeepId = bestGift?.variant_id;

  const giftsToRemove = existingGifts.filter(
    (gift) => gift.variant_id !== giftToKeepId, // remove all gifts except the best one
  );

  const giftAlreadyExists = existingGifts.some(
    (gift) => gift.variant_id === giftToKeepId,
  );

  const shouldAddGift = giftToKeepId && !giftAlreadyExists;

  console.log('shouldAddGift', { shouldAddGift })
  console.log('gift what?>?', {
    giftAlreadyExists,
    giftsToRemove,
    giftToKeepId,
    bestGift,
  })

  if (!shouldAddGift && !giftsToRemove.length) {
    return cartContent;
  }

  for (const item of giftsToRemove) {
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: { [item.key]: 0 } }),
      });
    } catch (err) {
      console.error("Error removing gift from cart:", err);
    }
  }

  if (shouldAddGift) {
    try {
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: giftToKeepId,
              quantity: 1,
              properties: {
                _free_gift: true,
                _gift_source_path: window.location.pathname,
                _gift_priority: bestGift.gift_priority, // track priority for future logic/debug
              },
            },
          ],
        }),
      });
    } catch (err) {
      console.error("Error adding gift to cart:", err);
    }
  }

  await new Promise((resolve) => setTimeout(resolve, 200));
  return fetch("/cart.js").then((res) => res.json());
}

document.addEventListener("DOMContentLoaded", () => {
  const selects = document.querySelectorAll("[data-complementary-select]");
  selects.forEach((select) => {
    select.addEventListener("change", function () {
      const selectedOption = select.options[select.selectedIndex];
      const url = selectedOption.dataset.url;
      if (url) {
        window.location.href = url;
      }
    });
  });

  // Free gift cookie for new visitors
  if (!document.cookie.includes("first_visit_time=")) {
    const now = Date.now();
    document.cookie = `first_visit_time=${now}; path=/; max-age=${31536000 * 2}`;
  }
});

function getCartDiscounts(cart) {
  const cartLevelDiscounts =
    cart.cart_level_discount_applications?.map((d) => ({
      title: d.title,
      type: d.value_type, // 'percentage' or 'fixed_amount'
      value: d.value,
      source: "cart",
    })) || [];

  const itemLevelDiscounts = cart.items
    .flatMap((item) => item.line_level_discount_allocations || [])
    .map((a) => {
      const d = a.discount_application;
      return d?.title
        ? {
            title: d.title,
            type: d.value_type,
            value: d.value,
            source: "item",
          }
        : null;
    })
    .filter(Boolean);

  const uniqueMap = new Map();

  [...cartLevelDiscounts, ...itemLevelDiscounts].forEach((d) => {
    const key = `${d.title}::${d.source}`;
    if (!uniqueMap.has(key)) uniqueMap.set(key, d);
  });

  return [...uniqueMap.values()];
}

async function updateAnnouncementBar(discounts) {
  const announcementBar = document.querySelector("announcement-bar");
  const slideList = announcementBar?.querySelector(".announcement-bar__list");

  if (!announcementBar || !slideList) return;
  slideList
    .querySelectorAll("[data-discount-announcement]")
    .forEach((el) => el.remove());

  if (discounts.length === 0) {
    announcementBar.items = [
      ...slideList.querySelectorAll("announcement-bar-item"),
    ];

    clearInterval(announcementBar._interval);

    if (announcementBar.items.length > 0) {
      await customElements.whenDefined("announcement-bar-item"); // <-- Wait for definition
      announcementBar.items.forEach((item, i) => (item.selected = i === 0));

      try {
        await announcementBar.select(0, true);
      } catch (err) {
        console.warn("Announcement bar select error (empty):", err);
      }

      if (announcementBar.autoPlay) {
        announcementBar._startPlayer?.();
      }
    }

    return;
  }

  discounts.forEach((d) => {
    const slide = document.createElement("announcement-bar-item");
    slide.setAttribute("data-discount-announcement", "");
    slide.className = "announcement-bar__item";
    slide.innerHTML = `
      <div class="announcement-bar__message text--xsmall" style="font-weight: normal;">
         <span>Your code ${d.title} has been applied for ${d.type === "percentage" ? ` ${parseFloat(d.value).toFixed(0)}% off` : ""} at checkout</span>
      </div>`;
    slideList.prepend(slide);
  });

  announcementBar.items = [
    ...slideList.querySelectorAll("announcement-bar-item"),
  ];

  await customElements.whenDefined("announcement-bar-item");

  announcementBar.items.forEach((item, index) => {
    item.selected = index === 0;
  });

  const current = announcementBar.items[announcementBar.selectedIndex];
  if (current && typeof current.deselect === "function") {
    try {
      await current.deselect(false);
    } catch (err) {
      console.warn("Announcement bar deselect error:", err);
    }
  }

  clearInterval(announcementBar._interval);

  try {
    await announcementBar.select(0, true);
  } catch (err) {
    console.warn("Announcement bar select error:", err);
  }

  if (announcementBar.autoPlay) {
    announcementBar._startPlayer?.();
  }
}

function updateMiniCartSavings(cart, discounts) {
  const labelEl = document.querySelector("[data-discount-text]");
  if (!labelEl) return;

  const totalSaved = (cart.total_discount || 0) / 100;

  const baseText = labelEl.getAttribute("data-discount-text") || "You’ve saved";
  let finalText = baseText;

  if (totalSaved > 0 && discounts.length > 0) {
    const titles = discounts.map((d) => d.title).join(", ");
    finalText += ` ${titles}`;
  }

  labelEl.textContent = finalText;
}

document.documentElement.addEventListener("cart:refresh", async () => {
  const cart = await fetch("/cart.js").then((r) => r.json());
  const discounts = getCartDiscounts(cart);
  console.log('cart discounts', {discounts})
  updateAnnouncementBar(discounts);
  requestAnimationFrame(() => {
    updateMiniCartSavings(cart, discounts);
  });
});
class TieredProgressBar extends HTMLElement {
  constructor() {
    super();
    this._els = {};
    this._markers = [];
    this._labels = [];
  }

  connectedCallback() {
    this._parseAttributes();
    this._buildStaticDOM();
    this._updateUI();
    document.documentElement.addEventListener("cart:updated", this._onCartUpdated.bind(this));
  }

  _parseAttributes() {
    const tiersAttr = this.getAttribute("data-tiers") || "";
    const currency = this.getAttribute("data-currency") || "USD";
    const subtotal = parseInt(this.getAttribute("data-subtotal") || "0", 10) || 0;
    this.currency = currency;
    this.subtotal = subtotal;
    this.market = this.getAttribute("data-market") || "";

    this.tiers = tiersAttr
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
      .map((pair) => {
        const idx = pair.indexOf(":");
        if (idx === -1) return null;
        const amount = parseInt(pair.slice(0, idx).trim(), 10) || 0;
        const label = pair.slice(idx + 1).trim();
        return { amount, label };
      })
      .filter(Boolean)
      .sort((a, b) => a.amount - b.amount);

    this.isSingle = this.tiers.length === 1;
    this.maxAmount = this.tiers.length ? this.tiers[this.tiers.length - 1].amount : 0;

    this.twoStopLayout =
      this.tiers.length === 2 && this.tiers.some(t => /gift|gwp/i.test(t.label));

    this.GUTTER_PCT = (this.isSingle || this.twoStopLayout) ? 0 : 25;
    this.usablePct = 100 - this.GUTTER_PCT;
  }

  _onCartUpdated(event) {
    if (!event.detail?.cart) return;
    const cart = event.detail.cart;

    let total = 0;
    for (const item of cart.items || []) {
      if (item.requires_shipping) total += item.final_line_price || 0;
    }

    let cartLevelDiscount = 0;
    for (const app of cart.cart_level_discount_applications || []) {
      cartLevelDiscount += app.total_allocated_amount || 0;
    }

    this.subtotal = Math.max(0, total - cartLevelDiscount);

    // Subscriptions in the US market qualify for free shipping
    const hasSubscription = (cart.items || []).some(
      (item) => item.selling_plan_allocation
    );
    if (hasSubscription && this.market === "us") {
      this.subtotal = this.maxAmount;
    }

    this._updateUI();
  }

  _buildStaticDOM() {
    this.classList.add("tiered-progress");
    this.classList.toggle("tiered-progress--single", this.isSingle);

    if (!this.tiers.length) {
      this.innerHTML = "";
      return;
    }

    const track = document.createElement("div");
    track.className = "tiered-progress__track";
    track.setAttribute("role", "progressbar");
    track.setAttribute("aria-valuemin", "0");
    track.setAttribute("aria-valuemax", String(this.maxAmount));

    const bar = document.createElement("div");
    bar.className = "tiered-progress__bar";

    const fill = document.createElement("div");
    fill.className = "tiered-progress__bar-fill";

    const markersWrap = document.createElement("div");
    markersWrap.className = "tiered-progress__markers";

    const labelsWrap = document.createElement("div");
    labelsWrap.className = "tiered-progress__labels";

    this._markers = [];
    this._labels = [];
    for (const t of this.tiers) {
      const left = this._posFor(t.amount);

      const m = document.createElement("div");
      m.className = "tiered-progress__marker is-locked";
      m.style.left = `${left}%`;
      m.setAttribute("title", t.label);
      markersWrap.appendChild(m);
      this._markers.push(m);

      const l = document.createElement("div");
      l.className = "tiered-progress__label is-locked";
      l.style.left = `${left}%`;
      l.textContent = t.label;
      labelsWrap.appendChild(l);
      this._labels.push(l);

      this._applyEdgeAttr(left, l, m);
    }

    bar.appendChild(fill);
    track.appendChild(bar);
    track.appendChild(markersWrap);
    track.appendChild(labelsWrap);

    const message = document.createElement("div");
    message.className = "tiered-progress__message";

    this.innerHTML = "";
    this.appendChild(track);
    this.appendChild(message);

    this._els = { track, fill, message, markersWrap, labelsWrap };
  }

  _edgeFor(leftPct) {
    const EDGE_EPS = 0.5;
    if (leftPct <= EDGE_EPS) return "start";
    if (leftPct >= 100 - EDGE_EPS) return "end";
    return "";
  }

  _applyEdgeAttr(leftPct, labelEl, markerEl) {
    const edge = this._edgeFor(leftPct);
    if (edge) {
      if (labelEl) labelEl.setAttribute("data-edge", edge);
      if (markerEl) markerEl.setAttribute("data-edge", edge);
    }
  }

  _updateUI() {
    if (!this.tiers.length) return;

    const subtotal = this.subtotal;
    const maxAmount = this.maxAmount;

    this._els.track.setAttribute("aria-valuenow", String(Math.min(subtotal, maxAmount)));

    let nextTier = null;
    let highestUnlocked = null;
    for (const tier of this.tiers) {
      if (subtotal >= tier.amount) highestUnlocked = tier;
      else if (!nextTier) nextTier = tier;
    }

    const message = nextTier
      ? `Spend ${this._formatMoney(nextTier.amount - subtotal)} more to unlock ${nextTier.label}`
      : `You unlocked ${highestUnlocked ? highestUnlocked.label : this.tiers[0].label}`;
    this._els.message.textContent = message;

    let targetPercent;
    if (this.twoStopLayout) {
      const low = this.tiers[0];
      const high = this.tiers[1];
      const half = this.usablePct / 2;

      if (subtotal <= 0) {
        targetPercent = 0;
      } else if (subtotal < low.amount) {
        targetPercent = Math.min(half, (subtotal / Math.max(1, low.amount)) * half);
      } else if (subtotal < high.amount) {
        const span = Math.max(1, high.amount - low.amount);
        const overLow = subtotal - low.amount;
        targetPercent = half + Math.min(half, (overLow / span) * half);
      } else {
        targetPercent = this.usablePct;
      }
    } else {
      targetPercent = Math.round(
        Math.min(this.usablePct, (subtotal / Math.max(1, maxAmount)) * this.usablePct)
      );
    }

    this._els.fill.style.width = `${targetPercent}%`;

    this.tiers.forEach((t, i) => {
      const unlocked = subtotal >= t.amount;
      this._markers[i].classList.toggle("is-unlocked", unlocked);
      this._markers[i].classList.toggle("is-locked", !unlocked);
      this._labels[i].classList.toggle("is-unlocked", unlocked);
      this._labels[i].classList.toggle("is-locked", !unlocked);
    });
  }

  _posFor(amount) {
    if (this.isSingle) return 100;
    if (this.twoStopLayout) return amount === this.maxAmount ? this.usablePct : this.usablePct / 2;
    const ratio = Math.max(0, Math.min(1, amount / Math.max(1, this.maxAmount)));
    return ratio * this.usablePct;
  }

  _formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const code = this.currency;
    const symbol =
      code === "USD" || code === "CAD" || code === "AUD" ? "$" :
      code === "GBP" ? "£" :
      code === "EUR" ? "€" : "";
    return symbol ? `${symbol}${amount}` : `${amount} ${code}`;
  }
}
customElements.define("tiered-progress-bar", TieredProgressBar);
